import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { ConfigService } from '../services/config.service';
import { INSIGHTS_API_REQUEST } from './http.context';

function normalizeBaseUrl(url: string): string {
  return url.replace(/\/$/, '');
}

/** Same scheme+host+port. Avoids prefix matches like `insights.example.com.evil.com`. */
function isSameOrigin(url: string, baseUrl: string): boolean {
  try {
    return new URL(url).origin === new URL(baseUrl).origin;
  } catch {
    return false;
  }
}

export const httpInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const configService = inject(ConfigService);
  const pmsBaseUrl = normalizeBaseUrl(configService.apiUrl());
  const insightsBaseUrl = normalizeBaseUrl(configService.insightsApiUrl());
  const isInsightsRequest =
    req.context.get(INSIGHTS_API_REQUEST) ||
    (Boolean(insightsBaseUrl) && isSameOrigin(req.url, insightsBaseUrl));

  let apiReq = req;

  if (!req.url.startsWith('http')) {
    const url = req.url.startsWith('/') ? req.url : `/${req.url}`;

    apiReq = req.clone({
      url: `${pmsBaseUrl}${url}`,
    });
  }

  const token = authService.getToken();
  const headers: Record<string, string> = {};

  // Insights now validates the same PMS session token (as an HS256 shared-secret token), so
  // forward the bearer to both PMS and Insights. Insights stays credential-less (no cookies).
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  apiReq = apiReq.clone({
    setHeaders: headers,
    // PMS uses cookie fallback; Insights CORS uses allow_origins=* (incompatible with credentials).
    withCredentials: !isInsightsRequest,
  });

  // Never clear the session here — API 401s (e.g. /llm-workflow) must not log the user out.
  // Session expiry is handled only by AuthService.fetchCurrentUser() for /auth/me.
  return next(apiReq);
};
